import type { Tone } from "./orders";
import type { Account } from "./types";

// What the Accounts screens work out from accounts (0076): a party's kind, status and model in words, its supplier's
// terms as a buyer says them ("2% 10, net 30"), and what links to its work elsewhere in the app. Pure, so it is tested
// without a browser.

export function modelWords(model: string | undefined): string {
  return model === "DIRECT" ? "Direct" : model === "AGENT" ? "Broker/Agent" : "";
}

const STATUS: Record<string, Tone> = { ACTIVE: "good", PROSPECT: "soon", DORMANT: "neutral", ENDED: "neutral" };

export function statusTone(status: string): Tone {
  return STATUS[status] ?? "neutral";
}

/** Customer, Supplier, or both: what the party is to Tare. */
export function kindWords(kinds: string[]): string {
  const k = kinds.map((x) => (x === "CUSTOMER" ? "Customer" : x === "SUPPLIER" ? "Supplier" : "Other"));
  return k.join(" and ");
}

/** A supplier's payment terms as a buyer says them: "2% 10, net 30", or what the terms say in words. */
export function termsWords(t: Account["terms"]): string {
  if (!t) return "";
  const parts: string[] = [];
  if (t.discountPct && t.discountDays) parts.push(`${t.discountPct}% ${t.discountDays}`);
  if (t.netDays) parts.push(`net ${t.netDays}`);
  return parts.length ? parts.join(", ") : t.payment ?? "";
}

/** Where a party's work is in the app: its orders, releases and products as a customer; invoices as a supplier. */
export function workLinks(a: Account): { text: string; href: string }[] {
  const q = (k: string) => `?${new URLSearchParams({ [k]: a.code })}`;
  const links: { text: string; href: string }[] = [];
  if (a.kinds.includes("CUSTOMER")) {
    links.push({ text: "Orders", href: `/orders${q("customer")}` }, { text: "Releases", href: `/releases${q("customer")}` },
      { text: "Products", href: `/products${q("customer")}` });
    if (a.model === "AGENT" || a.modelBySupplier?.some((r) => r.model === "AGENT")) links.push({ text: "Commissions", href: `/commissions${q("customer")}` });
  }
  if (a.kinds.includes("SUPPLIER")) {
    links.push({ text: "Invoices", href: `/invoices${q("supplier")}` });
    if (a.modelByCustomer?.some((r) => r.model === "AGENT")) links.push({ text: "Commissions", href: `/commissions${q("supplier")}` });
  }
  return links;
}

/** The one row for a code among search results (the search matches names too). */
export function byCode(rows: Account[], code: string): Account | undefined {
  return rows.find((r) => r.code.toUpperCase() === code.toUpperCase());
}
